// Problem reports / abuse reports persisted in Lovable Cloud.
import { supabase } from '@/integrations/supabase/client';
import { uploadDataUrl } from './storage';

export type ReportSubject = 'bug' | 'report_user' | 'report_content' | 'other';

export interface ReportInput {
  subject: ReportSubject | string;
  description: string;
  /** data-URL produced by the screenshot picker */
  screenshot?: string | null;
  targetType?: string | null;
  targetId?: string | null;
}

export async function submitReport(userId: string, input: ReportInput): Promise<{ error?: string }> {
  let screenshotUrl: string | undefined;
  if (input.screenshot) {
    screenshotUrl = await uploadDataUrl('report-media', userId, input.screenshot);
  }

  const { error } = await supabase.from('reports').insert({
    reporter_id: userId,
    subject: input.subject,
    description: input.description.trim(),
    screenshot_url: screenshotUrl ?? null,
    target_type: input.targetType ?? null,
    target_id: input.targetId ?? null,
  });

  return { error: error?.message };
}

export async function listMyReports(userId: string) {
  const { data, error } = await supabase
    .from('reports')
    .select('id, subject, description, status, created_at')
    .eq('reporter_id', userId)
    .order('created_at', { ascending: false })
    .limit(50);
  if (error) return { reports: [], error: error.message };
  return { reports: data ?? [] };
}

export interface AdminReport {
  id: string;
  reporter_id: string;
  subject: string;
  description: string;
  screenshot_url: string | null;
  target_type: string | null;
  target_id: string | null;
  status: string;
  admin_notes: string | null;
  created_at: string;
  updated_at: string;
  handled_at: string | null;
  handled_by: string | null;
}

/** Admin-only: every report submitted on the platform (RLS restricts this to admins). */
export async function listAllReports(): Promise<{ reports: AdminReport[]; error?: string }> {
  const { data, error } = await supabase
    .from('reports')
    .select('id, reporter_id, subject, description, screenshot_url, target_type, target_id, status, admin_notes, created_at, updated_at, handled_at, handled_by')
    .order('created_at', { ascending: false })
    .limit(200);
  if (error) return { reports: [], error: error.message };
  return { reports: (data ?? []) as AdminReport[] };
}

export async function updateReportStatus(
  reportId: string,
  status: 'open' | 'reviewing' | 'resolved' | 'dismissed',
  adminNotes?: string,
): Promise<{ error?: string }> {
  const { error } = await supabase
    .from('reports')
    .update({ status, ...(adminNotes !== undefined ? { admin_notes: adminNotes } : {}) })
    .eq('id', reportId);
  return { error: error?.message };
}

